import { useState } from "react";
import { api } from "@/lib/api";
import { StatCard, PageTitle, CARD, BTN, StatusPill, useList } from "@/pages/admin/ui";
import { Sparkles, Bell } from "lucide-react";

export default function Dashboard() {
  const { data: stats } = useList("/admin/dashboard/stats", []);
  const s = stats && !Array.isArray(stats) ? stats : {};
  const [insight, setInsight] = useState("");
  const [floading, setFloading] = useState(false);

  const runForecast = async () => {
    setFloading(true);
    try { const { data } = await api.get("/admin/forecast"); setInsight(data.insight); }
    catch { setInsight("Unable to generate forecast right now."); }
    finally { setFloading(false); }
  };

  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Executive Dashboard" subtitle="Live operational overview across events, food services and menu." />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="New Event Enquiries" value={s.new_events ?? "–"} accent />
        <StatCard label="New Bulk Enquiries" value={s.new_bulk ?? "–"} accent />
        <StatCard label="Confirmed Events" value={s.confirmed_events ?? "–"} />
        <StatCard label="Active Contracts" value={s.active_contracts ?? "–"} />
        <StatCard label="Menu Items" value={s.menu_count ?? "–"} hint={`${s.published_menu ?? 0} published`} />
        <StatCard label="Brands" value={s.brands ?? "–"} />
        <StatCard label="Branches" value={s.branches ?? "–"} />
        <StatCard label="Staff" value={s.staff ?? "–"} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className={`${CARD} p-5 lg:col-span-2`}>
          <h3 className="mb-4 font-display text-xl text-[#0F172A]">Upcoming Events</h3>
          {(s.upcoming_events || []).length === 0 ? <p className="text-sm text-[#94A3B8]">No upcoming confirmed events.</p> : (
            <div className="space-y-3">
              {(s.upcoming_events || []).map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-md border border-[#F1F5F9] p-3">
                  <div><div className="font-medium text-[#0F172A]">{e.name} · {e.event_type || "Event"}</div>
                    <div className="text-xs text-[#64748B]">{e.event_date || "Date TBD"} · {e.city || e.venue || "—"} · {e.guest_count || "?"} guests</div></div>
                  <StatusPill status={e.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`${CARD} p-5`}>
          <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#C5A059]" /><h3 className="font-display text-xl text-[#0F172A]">AI Forecast</h3></div>
          <button className={BTN + " w-full"} onClick={runForecast} disabled={floading} data-testid="dashboard-run-forecast">{floading ? "Analyzing…" : "Generate Insight"}</button>
          {insight && <div className="mt-4 whitespace-pre-wrap rounded-md bg-[#F8F9FA] p-3 text-sm leading-relaxed text-[#334155]">{insight}</div>}
        </div>
      </div>

      <div className={`${CARD} mt-6 p-5`}>
        <div className="mb-3 flex items-center gap-2"><Bell className="h-4 w-4 text-[#C5A059]" /><h3 className="font-display text-xl text-[#0F172A]">Recent Notifications</h3></div>
        {(s.recent_notifications || []).length === 0 ? <p className="text-sm text-[#94A3B8]">No notifications yet.</p> : (
          <div className="divide-y divide-[#F1F5F9]">
            {(s.recent_notifications || []).map((n) => (
              <div key={n.id} className="flex items-center justify-between py-2.5 text-sm">
                <span className={n.is_read ? "text-[#94A3B8]" : "font-medium text-[#0F172A]"}>{n.message}</span>
                <span className="text-xs text-[#94A3B8]">→ {n.target_phone}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
